import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { Trans, useLingui } from '@lingui/react/macro';

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLingui();
  // SSO pending-verification: the provider's email claim wasn't trusted
  // (see API sso-service trust model), so the login pauses here until the
  // emailed 6-digit code is entered. No account exists/merges before that.
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const success = searchParams.get('success');
        const data = searchParams.get('data');
        const error = searchParams.get('error');
        const verifyRequired = searchParams.get('verify_required');

        if (error) {
          navigate(`/login?error=${encodeURIComponent(error)}`);
          return;
        }

        if (verifyRequired === 'true' && searchParams.get('pending')) {
          setPendingToken(searchParams.get('pending'));
          setPendingEmail(searchParams.get('email') || '');
          // Strip the pending token from the address bar.
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }

        if (success === 'true' && data) {
          try {
            // Decode base64url auth data
            const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
            const decoded = atob(padded);
            const authData = JSON.parse(decoded);

            if (authData.tokens?.accessToken) {
              // Store token and user
              const token = authData.tokens.accessToken;
              const user = authData.user;

              authService['token'] = token;
              authService['user'] = user;

              localStorage.setItem('privacypal_token', token);
              localStorage.setItem('privacypal_user', JSON.stringify(user));

              // Set default auth header
              const axios = (await import('axios')).default;
              axios.defaults.headers.common['x-access-token'] = token;

              // Check onboarding status and route accordingly
              const { onboardingService } = await import('../services/onboardingService');
              const done = await onboardingService.getStatus();
              window.location.href = done ? '/dashboard' : '/onboarding';
              return;
            }
          } catch (decodeError) {
            console.error('Failed to decode auth data:', decodeError);
            navigate('/login?error=invalid_callback_data');
            return;
          }
        }

        // If we get here, authentication failed
        navigate('/login?error=authentication_failed');
      } catch (error) {
        console.error('OAuth callback error:', error);
        navigate('/login?error=callback_error');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!pendingToken || !/^\d{6}$/.test(code.trim())) {
      setCodeError(t`Enter the 6-digit code from the email.`);
      return;
    }
    setVerifyBusy(true);
    setCodeError('');
    const result = await authService.ssoComplete(pendingToken, code.trim());
    setVerifyBusy(false);
    if (result.success) {
      const { onboardingService } = await import('../services/onboardingService');
      const done = await onboardingService.getStatus().catch(() => false);
      window.location.href = done ? '/dashboard' : '/onboarding';
      return;
    }
    if (result.expiredSession) {
      navigate('/login?error=' + encodeURIComponent(t`Verification session expired. Please sign in again.`));
      return;
    }
    setCodeError(result.error || t`Verification failed`);
  };

  if (pendingToken) {
    return (
      <div className="p-4 sm:p-8" style={{ fontFamily: "'Inter', sans-serif", backgroundColor: 'var(--color-bg)', color: 'var(--color-text)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="max-w-md w-full">
          <div className="glass-panel rounded-3xl p-6 sm:p-8 border border-blue-100 dark:border-blue-900/30">
            <h2 className="text-xl font-semibold mb-2 text-center"><Trans>Verify your email</Trans></h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-6">
              <Trans>
                To keep your account secure, we need to confirm you own{' '}
                <strong>{pendingEmail}</strong>. If that address can receive mail, we've sent it a
                6-digit code — enter it below to finish signing in.
              </Trans>
            </p>
            {codeError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {codeError}
              </div>
            )}
            <form onSubmit={handleVerify} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setCodeError(''); }}
                className="w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-[0.5em] font-mono"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                autoFocus
              />
              <button
                type="submit"
                disabled={verifyBusy || code.length !== 6}
                className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
              >
                {verifyBusy ? t`Verifying…` : t`Verify & continue`}
              </button>
            </form>
            <p className="mt-3 text-xs text-center text-slate-500 dark:text-slate-400">
              <Trans>The code expires in 10 minutes. Check your spam folder if you don't see it.</Trans>
            </p>
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <Trans>Back to sign in</Trans>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" style={{ fontFamily: "'Inter', sans-serif", backgroundColor: 'var(--color-bg)', color: 'var(--color-text)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="text-center">
        <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
        <p className="text-slate-600 dark:text-slate-400"><Trans>Completing authentication...</Trans></p>
      </div>
    </div>
  );
}
